import { getUrl } from "aws-amplify/storage";
import { snakeCase } from 'lodash';
import moment from "moment";
import React, { useContext, useEffect, useReducer, useState } from "react";
import { useForm } from "react-hook-form";
import { FaCheckCircle, FaChevronLeft, FaChevronRight, FaDownload, FaLockOpen, FaRegCircle, FaSpinner, FaTimesCircle, FaTrash } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { fetchCompletedDocument, fetchCompletedDocuments, fetchCompletedDocumentsByCustomer, fetchCompletedDocumentsByForeman, fetchCompletedDocumentsByUser, findCompletedDocuments, removeCompletedDocument, selectDocument, selectDocuments, selectLoading, selectPagination, setActiveDocument } from "../common/slices/document";
import { resetPagination } from "../common/slices/globalActions";
import { selectActive } from "../common/slices/location";
import { pathNames } from "../constants";
import { ErrorContext } from "../ErrorContext";
import ActionContainer from "./ActionContainer";
import { BlackButton, ButtonBase, DarkButton, LightButton } from "./Buttons";
import FormFilterDropDown from "./FormFilterDropDown";
import Layout from "./Layout";
import ModalConfirm from "./ModalConfirm";
import ModalPDFPreview from "./ModalPDFPreview";
import { StyledSearch } from "./Search";
import { MenuItem, Table, TableData, TableDataLast, TableDataUnderline, TableFooter, TableHeader, TableMenu, TableRow } from "./Table";
import { H1 } from "./Typography";
import FormSafetyFormExport from "./FormExportSafetyFormReport";
import { UncontrolledCheckboxInput } from "./FormCheckbox";

const getSelectionItem = (doc) => ({
    id: doc.id,
    name: doc.name,
    timestamp: doc.createdAt || doc.timestamp || doc.submissionDate,
    createdAt: doc.createdAt,
    pdfKey: doc.pdfKey,
});

const selectionReducer = (state, action) => {
    switch (action.type) {
        case "TOGGLE": {
            const doc = action.payload;
            const id = typeof doc === "string" ? doc : doc.id;

            if (state[id]) {
                const { [id]: removed, ...nextState } = state;
                return nextState;
            }

            return {
                ...state,
                [id]: typeof doc === "string" ? { id } : getSelectionItem(doc),
            };
        }

        case "TOGGLE_ALL": {
            const { items = [], value } = action.payload;

            if (value) {
                return {
                    ...state,
                    ...items.reduce((acc, doc) => {
                        acc[doc.id] = getSelectionItem(doc);
                        return acc;
                    }, {}),
                };
            }

            const nextState = { ...state };

            items.forEach((doc) => {
                delete nextState[doc.id];
            });

            return nextState;
        }

        case "RESET":
            return {};

        default:
            return state;
    }
};

const SafetyFormsList = () => {
    const errorContext = useContext(ErrorContext);
    const nav = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const [showConfirm, setShowConfirm] = useState(false);
    const [selection, dispatchSelection] = useReducer(selectionReducer, {});
    const [downloading, setDownloading] = useState(false);

    const documents = useSelector(selectDocuments);
    const document = useSelector(selectDocument);
    const loading = useSelector(selectLoading);
    const pagination = useSelector(selectPagination);
    const locationId = useSelector(selectActive);

    const selectedDocuments = Object.values(selection);
    const selectionCount = selectedDocuments.length;
    const hasSelection = selectionCount > 0;
    const allSelected = documents.length > 0 && documents.every(doc => !!selection[doc.id]);

    const form = useForm({
        values: {
            draft: false,
            filterBy: { value: 'byLocation', label: "Location" },
            startDate: moment().add(-7, 'days').startOf('day').toDate(),
            endDate: moment().endOf('day').toDate(),
        }
    });

    const dispatcher = {
        byLocation: (filterParams, nextToken, storeNextToken) => dispatch(fetchCompletedDocuments(locationId, undefined, undefined, undefined, nextToken, storeNextToken)),
        byDate: (filterParams, nextToken, storeNextToken) => dispatch(fetchCompletedDocuments(locationId, undefined, filterParams.startDate, filterParams.endDate, nextToken, storeNextToken)),
        byAuthor: (filterParams, nextToken, storeNextToken) => dispatch(fetchCompletedDocumentsByUser(filterParams.crewMemberId.value, undefined, filterParams.startDate, filterParams.endDate, nextToken, storeNextToken)),
        byForeman: (filterParams, nextToken, storeNextToken) => dispatch(fetchCompletedDocumentsByForeman(filterParams.crewMemberId.value, undefined, filterParams.startDate, filterParams.endDate, nextToken, storeNextToken)),
        bySearch: (filterParams, nextToken, storeNextToken) => dispatch(findCompletedDocuments(locationId, filterParams.search, nextToken, storeNextToken)),
        byCustomer: (filterParams, nextToken, storeNextToken) => dispatch(fetchCompletedDocumentsByCustomer(filterParams.customerId.value, undefined, filterParams.startDate, filterParams.endDate, nextToken, storeNextToken)),
    }

    const onFilter = async (filterParams) => {
        try {
            dispatch(resetPagination());
            if (filterParams?.filterBy?.value) {
                await dispatcher[filterParams?.filterBy?.value](filterParams);
            }
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to get documents.", forSupport: ex.message });
        }
    }

    useEffect(() => {
        (async () => {
            try {
                if (locationId) {
                    await form.handleSubmit(onFilter)();
                }
            } catch (ex) {
                errorContext.setError({ errorMessage: "Failed to get documents.", forSupport: ex.message });
            }
        })()
    }, [locationId, dispatch])

    useEffect(() => {
        (async () => {
            try {
                if (id) {
                    await dispatch(fetchCompletedDocument(id));
                }
            } catch (ex) {
                errorContext.setError({ errorMessage: "Failed to get document.", forSupport: ex.message });
            }
        })()
    }, [id, dispatch]);

    const onDownloadClick = async (e, doc) => {
        try {
            e.stopPropagation();
            const url = await getUrl({ path: `public/${doc.pdfKey}`, options: { contentDisposition: `attachment; filename=${snakeCase(doc.name)}_${moment(doc.createdAt).format("MM-DD-YYYY-hhmm")}.pdf` } });
            window.open(url.url, "_blank");
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to get signed URL for document.", forSupport: ex.message });
        }
    };

    const onDownloadSelectedClick = async (e) => {
        try {
            e.stopPropagation();
            setDownloading(true);

            const selectedDocuments = Object.values(selection);

            await Promise.all(selectedDocuments.map(async (doc) => {
                // Replace this with your actual bulk/export function if needed.
                const url = await getUrl({
                    path: `public/${doc.pdfKey}`,
                    options: {
                        contentDisposition: `attachment; filename=${snakeCase(doc.name)}_${moment(doc.timestamp).format("MM-DD-YYYY-hhmm")}.pdf`
                    }
                });

                window.open(url.url, "_blank");
            }));
            dispatchSelection({ type: "RESET" })
        } catch (ex) {
            errorContext.setError({
                errorMessage: "Failed to download selected documents.",
                forSupport: ex.message
            });
        } finally {
            setDownloading(false);
        }
    };

    const onDeleteClick = async (e, doc) => {
        e.stopPropagation();
        setShowConfirm(doc);
    }

    const onConfirmDelete = async () => {
        try {
            await dispatch(removeCompletedDocument(showConfirm))
            setShowConfirm(false);
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to delete document.", forSupport: ex.message });
        }
    }


    const onNextPage = async (filterParams) => {
        try {
            await dispatcher[filterParams?.filterBy?.value](filterParams, pagination.nextToken);
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to get next document page.", forSupport: ex.message });
        }
    }

    const onPrevPage = async (filterParams) => {
        try {
            await dispatcher[filterParams?.filterBy?.value](filterParams, pagination.previousTokens[pagination.previousTokens.length - 2], false);
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to get previous document page.", forSupport: ex.message });
        }
    }

    const onSearch = async ({ search }) => {
        try {
            form.setValue('filterBy', { value: 'bySearch', label: 'Search' })
            form.setValue('search', search)
            await form.handleSubmit(onFilter)();
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to search documents.", forSupport: ex.message });
        }
    }

    const onReset = async () => {
        try {
            form.setValue('filterBy', { value: 'byLocation', label: 'Location' })
            await form.handleSubmit(onFilter)();
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to reset search.", forSupport: ex.message });
        }
    }

    const onUnlockDocument = async (e, doc) => {
        e.stopPropagation();
        try {
            // await dispatch(lockCompletedDocument(doc.id, null))
            throw new Error("Not implemented");
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to unlock document", forSupport: ex.message })
        }
    }

    const renderCreatedBy = (doc) => {
        if (doc?.creator?.name !== doc?.foreman?.name && doc.foreman) {
            return `${doc?.creator?.name} (${doc?.foreman?.name})`
        } else {
            return doc.creator?.name
        }
    }

    return (
        <Layout>
            <H1 style={{ marginTop: "2rem" }}>Safety Forms</H1>
            <ActionContainer>
                <div style={{ flex: 1 }} />
                <StyledSearch onSubmit={onSearch} onResetClick={onReset} />
                <FormFilterDropDown form={form} locationId={locationId} onFilter={onFilter} showDraftButton />
                <FormSafetyFormExport locationId={locationId} />
                <DarkButton onClick={() => nav(`${pathNames.SAFETYFORMS}/manage`)}>Manage Safety Forms</DarkButton>
                {hasSelection && <BlackButton onClick={onDownloadSelectedClick} disabled={downloading}>Download ({selectionCount})</BlackButton>}
                {hasSelection && <LightButton onClick={() => dispatchSelection({ type: "RESET" })} disabled={downloading}>Cancel</LightButton>}
            </ActionContainer>
            <Table>
                <thead>
                    <tr>
                        <TableHeader>
                            <UncontrolledCheckboxInput
                                containerStyle={{ margin: 0, padding: 0 }}
                                style={{ transform: 'none', width: '1rem', height: '1rem', lineHeight: '1rem' }}
                                errors={{}}
                                value={allSelected}
                                onChange={(v) => {
                                    dispatchSelection({
                                        type: "TOGGLE_ALL",
                                        payload: {
                                            items: documents,
                                            value: !allSelected,
                                        },
                                    })
                                }}
                            />
                        </TableHeader>
                        <TableHeader colSpan={2}>Form Name</TableHeader>
                        <TableHeader style={{ width: '25%' }}>Customer</TableHeader>
                        <TableHeader style={{ width: '25%' }}>Created</TableHeader>
                        <TableHeader style={{ width: '25%' }}>Created By (Foreman)</TableHeader>
                    </tr>
                </thead>
                <tbody>
                    {documents.map((doc) => (
                        <TableRow key={doc.id} onClick={() => nav(`${pathNames.SAFETYFORMS}/${doc.id}`)}>
                            <TableData onClick={e => e.stopPropagation()} style={{ width: '1rem', padding: 0, textAlign: 'center' }}>
                                <UncontrolledCheckboxInput
                                    containerStyle={{ margin: 0, padding: 0 }}
                                    style={{ transform: 'none', width: '1rem', height: '1rem', lineHeight: '1rem' }}
                                    errors={{}}
                                    value={selection[doc.id]}
                                    onChange={(v) => {
                                        dispatchSelection({ type: "TOGGLE", payload: doc })
                                    }}
                                />
                            </TableData>
                            <TableData style={{ width: '1rem' }}>{doc.draft ? <FaRegCircle title="Draft Document" /> : <FaCheckCircle title="Final Document" />}</TableData>
                            <TableDataUnderline>{doc.name}</TableDataUnderline>
                            <TableData>{doc.customer?.name}</TableData>
                            <TableData>{moment(doc.createdAt).format("MMM D, YYYY [at] h:mm A")}</TableData>
                            <TableDataLast>
                                {renderCreatedBy(doc)}

                                <TableMenu>
                                    {
                                        !!doc.lockedById &&
                                        <MenuItem onClick={(e) => onUnlockDocument(e, doc)}>
                                            Unlock
                                            <FaLockOpen style={{ marginLeft: "auto" }} />
                                        </MenuItem>
                                    }
                                    <MenuItem onClick={(e) => onDownloadClick(e, doc)}>
                                        Download
                                        <FaDownload style={{ marginLeft: "auto" }} />
                                    </MenuItem>
                                    <MenuItem onClick={(e) => onDeleteClick(e, doc)}>
                                        Delete
                                        <FaTrash style={{ marginLeft: "auto" }} />
                                    </MenuItem>
                                </TableMenu>
                            </TableDataLast>
                        </TableRow>
                    ))}
                    {
                        !loading && !documents.length && <TableRow><TableData colSpan={5}>No documents, please adjust your filter</TableData></TableRow>
                    }
                </tbody>
            </Table>
            <TableFooter>
                {loading && <FaSpinner className='fa-spin' />}
                <ButtonBase onClick={form.handleSubmit(onPrevPage)} disabled={!pagination.previousTokens[pagination.previousTokens.length - 1] || loading} style={{ color: 'black' }}><FaChevronLeft /></ButtonBase>
                <ButtonBase onClick={form.handleSubmit(onNextPage)} disabled={!pagination.nextToken || loading} style={{ color: 'black' }}><FaChevronRight /></ButtonBase>
            </TableFooter>

            {id &&
                <ModalPDFPreview document={document} onClose={() => {
                    dispatch(setActiveDocument(null));
                    nav(pathNames.SAFETYFORMS);
                }} />
            }
            {showConfirm &&
                <ModalConfirm
                    title="Delete Document?"
                    description={`Are you sure you want to delete the ${showConfirm.name} document created by ${showConfirm?.creator?.name} on ${moment(showConfirm.submissionDate).format("MMMM DD, YYYY [at] h:mm A")}?  This operation is permanent and cannot be undone.`}
                    onConfirm={onConfirmDelete}
                    onClose={() => setShowConfirm(false)}
                />
            }
        </Layout>
    );
};

export default SafetyFormsList;
